import React, { useEffect, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { capture } from 'ducks/tracking/actions'
import cn from 'classnames'
import Like from '@groovehq/internal-design-system/lib/assets/icons/facebook-messenger/Like'
import Negative from '@groovehq/internal-design-system/lib/assets/icons/Negative'
import ChatBotOutlinedV2 from '@groovehq/internal-design-system/lib/assets/icons/ChatBotOutlinedV2'
import Spinner from '@groovehq/internal-design-system/lib/components/Spinner/Spinner'
import EditorButton from 'components/EditorButton'
import useIsMounted from 'util/hooks/useIsMounted'
import SanitizedHTML from 'shared/components/ui/SanitizedHTML'
import { dumbLinkify } from 'util/strings'
import { useAi } from 'ducks/ai/hooks'
import { selectAccountGlobalId } from 'selectors/app/selectAccountId'
import { getRpcMethodFromActionId } from 'ducks/ai/operations'
import { selectCurrentTicketId } from 'selectors/tickets/current/selectCurrentTicketId'
import { buildId } from 'util/globalId'
import { getEditor, COMPOSER_ID } from 'shared/editor/utils'
import { doUpdateEditorVisibility } from 'ducks/editor/operations'
import { doOpenNotePage } from 'actions/pages'
import { selectIsAddingNote } from 'selectors/page'
import { handleDraftChange } from 'ducks/drafts2/operations'
import { selectDraftIdByTicketId } from 'ducks/drafts2/selectors'
import {
  CONVERSATION_TYPE,
  TEXT_REFINER_SERVICE,
  SUMMARY_ACTION,
} from 'ducks/ai/types'
import sparkles from 'assets/icons/emoji/sparkles.png'

const ALLOWED_AI_ACTIONS = [SUMMARY_ACTION]

const AIMessage = ({ onLoading }) => {
  const {
    requestAndWait,
    clearState,
    cancelRequest,
    requestState: {
      response,
      loading,
      loaded,
      error,
      rating,
      aiType,
      lastAiType,
    },
  } = useAi(CONVERSATION_TYPE)

  const ticketId = useSelector(selectCurrentTicketId)
  const accountId = useSelector(selectAccountGlobalId)
  const isAddingNote = useSelector(selectIsAddingNote)
  const globalTicketId = buildId('Conversation', ticketId)
  const dispatch = useDispatch()

  const [showThankyou, setShowThankyou] = useState(true)
  const [showCopied, setShowCopied] = useState(false)
  const isMounted = useIsMounted()

  const handleStopGenerating = useCallback(
    () => {
      capture(`ai ${aiType} canceled`, {
        ticket_id: globalTicketId,
        account_id: accountId,
      })
      cancelRequest()
    },
    [cancelRequest, aiType, globalTicketId, accountId]
  )
  const draftId = useSelector(state =>
    selectDraftIdByTicketId(state, ticketId, 'note')
  )
  const handleCopyToClipboard = useCallback(
    async () => {
      // when editor is available, just add the response. If not, save it as a draft
      // and then open the editor
      if (isAddingNote) {
        const tinyEditor = getEditor(COMPOSER_ID)
        tinyEditor.insertContent(response)
      } else {
        await handleDraftChange(dispatch, 'note', draftId, ticketId, null, {
          isNote: true,
          body: response,
        })
        await dispatch(doOpenNotePage(ticketId))
        await dispatch(doUpdateEditorVisibility(true))
        setShowCopied(true)
        capture(`ai ${lastAiType} saved to note`, {
          ticket_id: globalTicketId,
          account_id: accountId,
        })
      }
    },
    [
      dispatch,
      ticketId,
      response,
      globalTicketId,
      accountId,
      lastAiType,
      draftId,
      isAddingNote,
    ]
  )

  const handleRemoveAndClose = useCallback(
    () => {
      clearState()
      setShowThankyou(false)
    },
    [clearState]
  )

  const handleOnRating = useCallback(
    async buttonRating => {
      setShowThankyou(true)
      capture(`ai ${lastAiType} rated`, {
        ticket_id: globalTicketId,
        account_id: accountId,
        rating: buttonRating,
        content: response,
      })
    },
    [lastAiType, response, globalTicketId, accountId]
  )

  const handleGoodRating = useCallback(
    () => {
      handleOnRating(1)
    },
    [handleOnRating]
  )

  const handleBadRating = useCallback(
    () => {
      handleOnRating(-1)
    },
    [handleOnRating]
  )

  const prepareParams = useCallback(
    id => {
      switch (id) {
        case SUMMARY_ACTION:
          return { conversationId: globalTicketId }
        default:
          return {}
      }
    },
    [globalTicketId]
  )

  const doAiRequest = useCallback(
    async () => {
      const params = prepareParams(aiType)
      const method = getRpcMethodFromActionId(aiType)
      capture(`ai ${aiType} request`, {
        ticket_id: globalTicketId,
        account_id: accountId,
      })
      await requestAndWait({
        service: TEXT_REFINER_SERVICE,
        method,
        params,
      })
    },
    [requestAndWait, aiType, prepareParams, globalTicketId, accountId]
  )

  const handleOnTryAgain = useCallback(
    () => {
      doAiRequest(aiType)
    },
    [doAiRequest, aiType]
  )

  useEffect(
    () => {
      if (aiType && ALLOWED_AI_ACTIONS.includes(aiType)) {
        doAiRequest(aiType)
      }
    },
    [aiType, doAiRequest]
  )

  useEffect(
    () => {
      if (loading && onLoading) {
        onLoading()
      }
    },
    [loading, onLoading]
  )

  useEffect(
    () => {
      clearState()
    },
    [ticketId, clearState]
  )

  useEffect(
    () => {
      if (showThankyou) {
        setTimeout(() => {
          if (isMounted()) setShowThankyou(false)
        }, 5000)
      }
    },
    [showThankyou, isMounted]
  )

  useEffect(
    () => {
      if (showCopied) {
        setTimeout(() => {
          if (isMounted()) setShowCopied(false)
        }, 3000)
      }
    },
    [showCopied, isMounted]
  )

  if (!loading && !loaded && !error && !response) return null

  return (
    <div className={cn('changeset expanded ai', { error: !!error })}>
      <div className="action-container">
        {loading && (
          <div className="action-info">
            <div className="info-container">
              <Spinner className="info-icon" size="medium" />
              <div className="info-header">
                <img src={sparkles} alt="sparkles" /> AI Generation in progress…
              </div>
              <div>Note: This will only be shown to you</div>
              <div className="info-buttons">
                <EditorButton
                  type="info"
                  size="small"
                  onClick={handleStopGenerating}
                >
                  Stop generating
                </EditorButton>
              </div>
            </div>
          </div>
        )}
        {error && (
          <div className="action-info">
            <div className="info-container">
              <Negative className="info-icon" />
              <div className="info-header">Oh no, something went wrong!</div>
              <div>{error.message}</div>
              <div className="info-buttons">
                <EditorButton
                  type="info"
                  size="small"
                  onClick={handleOnTryAgain}
                >
                  Try again
                </EditorButton>
                <EditorButton
                  type="warningOutlineFilledHover"
                  size="small"
                  onClick={handleRemoveAndClose}
                >
                  Cancel
                </EditorButton>
              </div>
            </div>
          </div>
        )}
        {loaded &&
          response && (
            <div className="action">
              <div className="contents">
                <div className="top-side">
                  <ChatBotOutlinedV2 className="avatar" />
                  <div className="header-container">
                    <div className="header">
                      <div className="byline-container">
                        <div className="byline">
                          <span className="author">
                            AI Generated Explanation
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="description-section">
                      Note: This won{"'"}t be seen by other agents or the
                      customer
                    </div>
                  </div>
                </div>
                <div className="middle-side">
                  <SanitizedHTML
                    className="body"
                    html={dumbLinkify(response, { showFullUrl: true })}
                  />
                </div>
                <div className="bottom-side">
                  <div className="ai-buttons">
                    <EditorButton
                      type="info"
                      size="small"
                      onClick={handleCopyToClipboard}
                    >
                      {showCopied && 'Copied'}
                      {!showCopied && 'Insert into Note'}
                    </EditorButton>
                    <EditorButton
                      type="tertiary"
                      size="small"
                      onClick={handleRemoveAndClose}
                    >
                      Remove & Close
                    </EditorButton>
                  </div>
                  <div className="ai-ratings">
                    <div className="ai-rating-description">
                      {!rating && 'Rate this AI generation:'}
                      {showThankyou && '✨ Thanks for the rating'}
                    </div>
                    {!rating &&
                      !showThankyou && (
                        <div className="ai-rating-buttons">
                          <Like onClick={handleGoodRating} />
                          <Like onClick={handleBadRating} />
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  )
}

export default AIMessage
